import { SERVICES } from "@/lib/services";

export type ContactIntent = "callback" | "message";

export type PageContextKind =
  | "home"
  | "about"
  | "collab"
  | "services"
  | "service"
  | "blog"
  | "blog-post"
  | "blog-category"
  | "blog-tag"
  | "guide"
  | "guide-post"
  | "guide-category"
  | "resource"
  | "resource-post"
  | "resource-category"
  | "portfolio"
  | "portfolio-project"
  | "portfolio-category"
  | "portfolio-industry"
  | "portfolio-company"
  | "blueprint"
  | "scan"
  | "other";

export type ContactPageContext = {
  kind: PageContextKind;
  title: string;
  path: string;
  topic?: string;
  service?: string;
};

const KIND_LABEL: Record<PageContextKind, string> = {
  home: "Homepage",
  about: "About",
  collab: "Collab",
  services: "Services",
  service: "Service",
  blog: "Blog",
  "blog-post": "Article",
  "blog-category": "Blog category",
  "blog-tag": "Blog tag",
  guide: "Guides",
  "guide-post": "Guide",
  "guide-category": "Guide category",
  resource: "Resources",
  "resource-post": "Resource",
  "resource-category": "Resource category",
  portfolio: "Work",
  "portfolio-project": "Case study",
  "portfolio-category": "Work category",
  "portfolio-industry": "Industry",
  "portfolio-company": "Company",
  blueprint: "Blueprint",
  scan: "Scan",
  other: "Page",
};

export function kindLabel(kind: PageContextKind): string {
  return KIND_LABEL[kind];
}

export function serviceLabel(slug?: string): string | undefined {
  if (!slug) return undefined;
  return SERVICES.find((s) => s.slug === slug)?.label;
}

export function buildContactHref(
  context: ContactPageContext,
  options?: { intent?: ContactIntent; placement?: string },
): string {
  const params = new URLSearchParams();
  params.set("intent", options?.intent ?? "callback");
  params.set("from", context.kind);
  params.set("title", context.title.slice(0, 160));
  params.set("path", context.path);
  if (context.topic) params.set("topic", context.topic.slice(0, 80));
  if (context.service) params.set("service", context.service);
  if (options?.placement) params.set("placement", options.placement);
  return `/contact?${params.toString()}`;
}

export function parseContactContext(params: URLSearchParams): ContactPageContext | null {
  const kind = params.get("from") as PageContextKind | null;
  const title = params.get("title")?.trim();
  const path = params.get("path")?.trim();
  if (!kind || !title || !path) return null;
  if (!(kind in KIND_LABEL)) return null;
  return {
    kind,
    title,
    path,
    topic: params.get("topic")?.trim() || undefined,
    service: params.get("service")?.trim() || undefined,
  };
}

export function parseContactIntent(params: URLSearchParams): ContactIntent {
  return params.get("intent") === "callback" ? "callback" : "message";
}

export function contextSummary(context: ContactPageContext): string {
  const type = kindLabel(context.kind);
  const service = serviceLabel(context.service);
  const parts = [`${type}: ${context.title}`];
  if (context.topic) parts.push(context.topic);
  if (service) parts.push(service);
  return parts.join(" · ");
}

export function defaultMessageForContext(
  context: ContactPageContext,
  intent: ContactIntent,
): string {
  const want = intent === "callback" ? "I'd like a callback" : "I'd like to talk";
  const about = context.topic ? ` (${context.topic})` : "";
  const service = serviceLabel(context.service);
  const serviceLine = service ? ` Interested in ${service}.` : "";
  return `${want} about “${context.title}”${about}.${serviceLine}\nCame from ${context.path}`;
}

export function leadContextBlock(
  context: ContactPageContext | null,
  intent: ContactIntent,
  placement?: string,
): string {
  if (!context) {
    return [`Intent: ${intent === "callback" ? "Request a callback" : "Contact message"}`, placement && `Placement: ${placement}`]
      .filter(Boolean)
      .join("\n");
  }
  return [
    `Intent: ${intent === "callback" ? "Request a callback" : "Contact message"}`,
    `Page type: ${kindLabel(context.kind)}`,
    `Page: ${context.title}`,
    `URL: ${context.path}`,
    context.topic && `Topic: ${context.topic}`,
    serviceLabel(context.service) && `Service: ${serviceLabel(context.service)}`,
    placement && `Placement: ${placement}`,
  ]
    .filter(Boolean)
    .join("\n");
}

export function contextFromPath(pathname: string): ContactPageContext {
  const path = pathname || "/";
  if (path === "/") return { kind: "home", title: "Homepage", path: "/" };
  if (path === "/about") return { kind: "about", title: "About doimpo", path };
  if (path === "/collab") return { kind: "collab", title: "Collab Multiverse", path };
  if (path === "/services") return { kind: "services", title: "Services", path };
  if (path === "/portfolio") return { kind: "portfolio", title: "Selected work", path };
  if (path === "/blog") return { kind: "blog", title: "Blog", path };
  if (path === "/guides") return { kind: "guide", title: "Guides", path };
  if (path === "/resources") return { kind: "resource", title: "Resources", path };
  if (path === "/blueprint") return { kind: "blueprint", title: "Growth blueprint", path };
  if (path === "/scan") return { kind: "scan", title: "Growth scan", path };

  const serviceMatch = path.match(/^\/services\/([^/]+)$/);
  if (serviceMatch) {
    const slug = serviceMatch[1];
    return {
      kind: "service",
      title: serviceLabel(slug) ?? slug,
      path,
      service: slug,
    };
  }

  const blogCat = path.match(/^\/blog\/category\/([^/]+)$/);
  if (blogCat) return { kind: "blog-category", title: prettySlug(blogCat[1]), path, topic: prettySlug(blogCat[1]) };
  const blogTag = path.match(/^\/blog\/tag\/([^/]+)$/);
  if (blogTag) return { kind: "blog-tag", title: prettySlug(blogTag[1]), path, topic: prettySlug(blogTag[1]) };
  const guideCat = path.match(/^\/guides\/category\/([^/]+)$/);
  if (guideCat) return { kind: "guide-category", title: prettySlug(guideCat[1]), path, topic: prettySlug(guideCat[1]) };
  const resourceCat = path.match(/^\/resources\/category\/([^/]+)$/);
  if (resourceCat) {
    return { kind: "resource-category", title: prettySlug(resourceCat[1]), path, topic: prettySlug(resourceCat[1]) };
  }
  const portCat = path.match(/^\/portfolio\/category\/([^/]+)$/);
  if (portCat) return { kind: "portfolio-category", title: prettySlug(portCat[1]), path, topic: prettySlug(portCat[1]) };
  const portInd = path.match(/^\/portfolio\/industry\/([^/]+)$/);
  if (portInd) return { kind: "portfolio-industry", title: prettySlug(portInd[1]), path, topic: prettySlug(portInd[1]) };
  const portCo = path.match(/^\/portfolio\/company\/([^/]+)$/);
  if (portCo) return { kind: "portfolio-company", title: prettySlug(portCo[1]), path, topic: prettySlug(portCo[1]) };

  if (path.startsWith("/blog/")) return { kind: "blog-post", title: prettySlug(path.split("/")[2] ?? "Article"), path };
  if (path.startsWith("/guides/")) return { kind: "guide-post", title: prettySlug(path.split("/")[2] ?? "Guide"), path };
  if (path.startsWith("/resources/")) {
    return { kind: "resource-post", title: prettySlug(path.split("/")[2] ?? "Resource"), path };
  }
  if (path.startsWith("/portfolio/")) {
    return { kind: "portfolio-project", title: prettySlug(path.split("/")[2] ?? "Case study"), path };
  }

  return { kind: "other", title: prettySlug(path.replace(/^\//, "")) || "doimpo", path };
}

function prettySlug(slug: string): string {
  return decodeURIComponent(slug)
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function readyToGrowCopy(context: ContactPageContext): { headline: string; description: string } {
  switch (context.kind) {
    case "blog-post":
      return {
        headline: "Ready to grow?",
        description: `You just read “${context.title}.” Let’s turn that thinking into a plan for your business.`,
      };
    case "guide-post":
      return {
        headline: "Ready to grow?",
        description: `This playbook is a start. We’ll help you execute it—strategy, brand, product, and growth in one room.`,
      };
    case "resource-post":
      return {
        headline: "Ready to grow?",
        description: `Use the resource, then talk to us about putting it to work in your company.`,
      };
    case "portfolio-project":
      return {
        headline: "Ready to grow?",
        description: `Want a similar outcome to ${context.title}? Tell us what you’re building next.`,
      };
    case "portfolio-company":
      return {
        headline: "Ready to grow?",
        description: `See how we work with teams like ${context.title}—then let’s map yours.`,
      };
    case "portfolio-category":
    case "portfolio-industry":
      return {
        headline: "Ready to grow?",
        description: `Looking at ${context.title} work. We’ll shape a version of this for your market.`,
      };
    case "service":
      return {
        headline: "Ready to grow?",
        description: `Let’s discuss ${context.title.toLowerCase()} in the context of your roadmap—not as a one-off vendor job.`,
      };
    case "services":
      return {
        headline: "Ready to grow?",
        description: "Tell us what you’re building—we’ll map the right services under one partner.",
      };
    case "about":
      return {
        headline: "Ready to grow?",
        description: "One partner for strategy, brand, product, and growth. Let’s talk about yours.",
      };
    case "collab":
      return {
        headline: "Ready to grow?",
        description: "One system across strategy, creative, marketing, and code. Let’s put it to work.",
      };
    case "blueprint":
      return {
        headline: "Ready to grow?",
        description: "Got the roadmap? Book a callback and we’ll walk the next 90 days with you.",
      };
    case "blog-category":
    case "guide-category":
    case "resource-category":
    case "blog-tag":
      return {
        headline: "Ready to grow?",
        description: `You’re exploring ${context.title}. Tell us the outcome you want and we’ll take it from there.`,
      };
    default:
      return {
        headline: "Ready for one partner?",
        description: "Book a call—walk away with a clear next step.",
      };
  }
}
